import React, { useState, useContext, useEffect } from 'react';
import { AccountContext } from '../../components/AccountContext';
import { Panel } from 'primereact/panel';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { defineMessages, useIntl } from 'react-intl';
import gql from 'graphql-tag';
import { useHistory, useLocation } from 'react-router-dom';
import moment from 'moment-timezone';
import { NotificationContext } from '../../components/GrowlNotifications';
import { Loading } from '../../components/Loading';
import { growlErrors } from '../../utils/NotificationUtil';
import { useQuery } from '@apollo/react-hooks';
import { AppConstants } from '../../shared/Constants';
import { getNumberOfEmployeesLabel, buildSubscriptionPeriodText } from '../../utils/AppUtil';
import { parse, sortUrl, toSortOrder } from '../../utils/UrlUtil';
import OrganisationInvoicesTableHeader from './OrganisationInvoicesTableHeader';
import Invoice from '../../components/Invoice';

const ORGANISATION_INVOICES_QUERY = gql`
  query organisationInvoicesQuery(
    $accountId: ID!
    $sortBy: InvoiceSortBy
    $sortDir: SortDirection
    $filter: InvoiceFilter
  ) {
    account(id: $accountId) {
      id
      invoicesPaidForClients(sortBy: $sortBy, sortDir: $sortDir, filter: $filter) {
        id
        clientId
        clientName
        invoiceId
        invoiceNumber
        paymentDate
        paymentAmount
        subscriptionStartDate
        subscriptionEndDate
        numberOfEmployees
        subscriptionPeriodDuration
      }
    }
  }
`;

const i18nMessages = defineMessages({
  invoices: {
    id: 'menu.invoices',
    defaultMessage: 'Invoices',
  },
  clientId: { id: 'account.invoices.account-id', defaultMessage: 'Account ID' },
  clientName: { id: 'account.invoices.account-name', defaultMessage: 'Account name' },
  invoice: { id: 'account.invoices.invoice', defaultMessage: 'Invoice' },
  paid: { id: 'account.invoices.paid', defaultMessage: 'Paid' },
  amount: { id: 'account.invoices.amount', defaultMessage: 'Amount' },
  startDate: { id: 'account.invoices.start-date', defaultMessage: 'Start date' },
  period: { id: 'account.invoices.period', defaultMessage: 'Period' },
  renewalDate: { id: 'account.invoices.renewal-date', defaultMessage: 'Renewal date' },
  internalRespondents: { id: 'account.invoices.internal-respondents', defaultMessage: 'Int. Resp.' },
  month: {
    id: 'account.subscription.month',
    defaultMessage: 'month',
  },
  months: {
    id: 'account.subscription.months',
    defaultMessage: 'months',
  },
});

const OrganisationInvoices = () => {
  const currentAccount = useContext(AccountContext);
  const growl = useContext(NotificationContext);
  const { formatMessage } = useIntl();

  const history = useHistory();
  const location = useLocation();

  const params = parse(location.search);
  const variables = {
    accountId: currentAccount,
    sortBy: (params.sort && params.sort.sortBy) || 'clientId',
    sortDir: (params.sort && params.sort.sortDir) || 'ASC',
    filter: params.filter || { query: '' },
  };

  const { sort = {} } = params;
  const { sortBy, sortDir } = sort;

  const [invoices, setInvoices] = useState([]);

  const { loading, error, data } = useQuery(ORGANISATION_INVOICES_QUERY, {
    variables,
    fetchPolicy: 'no-cache',
  });

  useEffect(() => {
    if (data && data.account) {
      setInvoices(data.account.invoicesPaidForClients);
    }
    // eslint-disable-next-line
  }, [data]);

  if (loading) {
    return <Loading />;
  }

  if (error) {
    growlErrors(growl, error);
  }

  const invoiceNumberBodyTemplate = (rowData) => {
    return (
      <Invoice
        managingAccountId={currentAccount}
        clientAccountId={rowData.clientId}
        invoiceId={rowData.invoiceId}
        invoiceNumber={rowData.invoiceNumber}
      />
    );
  };

  const paymentDateBodyTemplate = (rowData) => {
    return <div>{moment(rowData.paymentDate).format(AppConstants.DATE_INPUT_FORMAT)}</div>;
  };

  const paymentAmountBodyTemplate = (rowData) => {
    return <div>${rowData.paymentAmount.toLocaleString()}</div>;
  };

  const subscriptionStartDateBodyTemplate = (rowData) => {
    return <div>{moment(rowData.subscriptionStartDate).format(AppConstants.DATE_INPUT_FORMAT)}</div>;
  };

  const subscriptionEndDateBodyTemplate = (rowData) => {
    return <div>{moment(rowData.subscriptionEndDate).format(AppConstants.DATE_INPUT_FORMAT)}</div>;
  };

  const numberOfEmployeesBodyTemplate = (rowData) => {
    return <div>{getNumberOfEmployeesLabel(rowData.numberOfEmployees)}</div>;
  };

  const subscriptionPeriodBodyTemplate = (rowData) => {
    return (
      <div>
        {buildSubscriptionPeriodText(
          formatMessage(i18nMessages.months),
          formatMessage(i18nMessages.month),
          rowData.subscriptionPeriodDuration,
        )}
      </div>
    );
  };

  return (
    <div className="p-grid">
      <div className="p-col-12">
        <div className="brand-detailed-wrapper">
          <Panel header={formatMessage(i18nMessages.invoices)}>
            <>
              <div className="p-col-12">
                <OrganisationInvoicesTableHeader />
              </div>
              <div className="p-col-12 align-center">
                <DataTable
                  value={invoices}
                  responsive
                  sortField={sortBy}
                  sortOrder={toSortOrder(sortDir)}
                  onSort={(event) => sortUrl(event, location, history)}
                  lazy
                  loading={loading}
                >
                  <Column field="clientId" header={formatMessage(i18nMessages.clientId)} sortable />
                  <Column field="clientName" header={formatMessage(i18nMessages.clientName)} sortable />
                  <Column
                    field="invoiceHostedUrl"
                    header={formatMessage(i18nMessages.invoice)}
                    body={invoiceNumberBodyTemplate}
                    currentAccount={currentAccount}
                  />
                  <Column
                    field="paymentDate"
                    header={formatMessage(i18nMessages.paid)}
                    body={paymentDateBodyTemplate}
                    sortable
                  />
                  <Column
                    field="paymentAmount"
                    header={formatMessage(i18nMessages.amount)}
                    body={paymentAmountBodyTemplate}
                  />
                  <Column
                    field="subscriptionStartDate"
                    header={formatMessage(i18nMessages.startDate)}
                    body={subscriptionStartDateBodyTemplate}
                  />
                  <Column
                    field="subscriptionPeriodDuration"
                    header={formatMessage(i18nMessages.period)}
                    body={subscriptionPeriodBodyTemplate}
                    sortable
                  />
                  <Column
                    field="subscriptionEndDate"
                    header={formatMessage(i18nMessages.renewalDate)}
                    body={subscriptionEndDateBodyTemplate}
                  />
                  <Column
                    field="numberOfEmployees"
                    header={formatMessage(i18nMessages.internalRespondents)}
                    body={numberOfEmployeesBodyTemplate}
                    sortable
                  />
                </DataTable>
              </div>
            </>
          </Panel>
        </div>
      </div>
    </div>
  );
};

export default OrganisationInvoices;
